import { Injectable } from '@angular/core';
import { MessageModel, SnackbarService } from '@services/ui/snackbar.service';
import LogRocket from 'logrocket';
import { from, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { Clipboard } from '@angular/cdk/clipboard';

const defaultSuccessMessage: MessageModel = {
	text: 'Copied to clipboard!',
	type: 'success',
};
const defaultFailureMessage: MessageModel = {
	text: 'Unable to copy to clipboard',
	type: 'error',
};

enum TYPES {
	PLAIN = 'text/plain',
	HTML = 'text/html',
}

interface TextClipboardItem {
	[TYPES.PLAIN]: Blob;
}

interface HtmlClipboardItem {
	[TYPES.HTML]: Blob;
}

export interface NotificationModel {
	success?: MessageModel;
	failure?: MessageModel;
}

export type NotificationOptions = boolean | NotificationModel;

@Injectable({
	providedIn: 'root',
})
export class ClipboardService {
	constructor(
		private snackbarService: SnackbarService,
		private clipboard: Clipboard,
	) {}

	copyHtml(html: string, notifyWhenDone?: NotificationOptions): void {
		const trying = { html };
		try {
			const item = new ClipboardItem({
				...this.makeTextClipboardObject(html),
				...this.makeHtmlClipboardObject(html),
			});
			this.copyToClipboard([item], trying, notifyWhenDone);
		} catch (err) {
			this.htmlCopyError(err);
			this.copyToClipboardSimple(html, trying, notifyWhenDone);
		}
	}

	copyText(text: string, notifyWhenDone?: NotificationOptions): void {
		const trying = { text };
		try {
			const item = new ClipboardItem({
				...this.makeTextClipboardObject(text),
			});
			this.copyToClipboard([item], trying, notifyWhenDone);
		} catch (err) {
			this.copyToClipboardSimple(text, trying, notifyWhenDone);
		}
	}

	// log the error, unless we know it's because of firefox/clipboardItem not being available without
	// a setting adjustment
	private htmlCopyError(err: unknown) {
		const errorMessage = err?.toString();
		if (errorMessage && /clipboarditem/i.test(errorMessage)) {
			console.warn(
				'Your browser does not appear to support html copying without adjusting a setting.',
			);
			console.warn(
				'Check about:config in a new tab, and ensure the dom.events.asyncClipboard.clipboardItem is set to true',
			);
		}

		LogRocket.error('unhandled error when copying to clipboard', err);
	}

	private makeTextClipboardObject(text: string): TextClipboardItem {
		return {
			[TYPES.PLAIN]: new Blob([text], { type: TYPES.PLAIN }),
		};
	}

	private makeHtmlClipboardObject(html: string): HtmlClipboardItem {
		return {
			[TYPES.HTML]: new Blob([html], { type: TYPES.HTML }),
		};
	}

	private copyToClipboard(
		items: ClipboardItem[],
		tried: Record<string, unknown>,
		notifyWhenDone?: NotificationOptions,
	): void {
		from(navigator.clipboard.write(items))
			.pipe(
				take(1),
				map(() => true),
				catchError((err) => {
					LogRocket.error('unable to copyToClipboard', {
						err,
						tried,
					});

					return of(false);
				}),
			)
			.subscribe((success) => {
				if (notifyWhenDone) {
					const message = success
						? (notifyWhenDone as NotificationModel)?.success ||
							defaultSuccessMessage
						: (notifyWhenDone as NotificationModel)?.failure ||
							defaultFailureMessage;

					this.snackbarService.openSnackBar(message);
				}
			});
	}

	private copyToClipboardSimple(
		item: string,
		tried: Record<string, unknown>,
		notifyWhenDone?: NotificationOptions,
	) {
		const success = this.clipboard.copy(item);
		let message: MessageModel;

		if (success) {
			if (notifyWhenDone) {
				message =
					(notifyWhenDone as NotificationModel)?.success ||
					defaultSuccessMessage;
			}
		} else {
			message =
				(notifyWhenDone as NotificationModel)?.failure || defaultFailureMessage;
			LogRocket.error('unable to copyToClipboard', {
				tried,
			});
		}

		if (message) {
			this.snackbarService.openSnackBar(message);
		}
	}
}
